/**
 * "Have a school code?" — the same small form on the waiting screen and on the
 * profile page. Entering a valid code approves the account and brings in the
 * school's classes, so on success the surrounding page simply re-renders.
 */

import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from '../../lib/utils';
import { inkMuted } from './learnShared';
import { useLearnAuth } from './LearnAuthProvider';

export function SchoolCodeForm({ className }: { className?: string }) {
  const { joinSchool, schoolCodeError } = useLearnAuth();
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    await joinSchool(code);
    setBusy(false);
  };

  return (
    <form onSubmit={submit} className={cn('flex flex-col gap-2 text-left', className)}>
      <label htmlFor="learn-school-code" className={cn('text-[11px] font-bold uppercase tracking-[0.12em]', inkMuted)}>
        School code
      </label>
      <div className="flex gap-2">
        <input
          id="learn-school-code"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          placeholder="From your teacher"
          autoCapitalize="characters"
          autoComplete="off"
          spellCheck={false}
          maxLength={32}
          className={cn(
            'h-11 min-w-0 flex-1 rounded-xl border border-[#191831]/[0.16] bg-white/95 px-3.5 text-[14.5px] uppercase tracking-[0.08em] text-[#191831]',
            'outline-none placeholder:normal-case placeholder:tracking-normal placeholder:text-[#78748F] focus:border-[#5B2EE5]',
            'dark:border-white/[0.16] dark:bg-[#0B0C15]/85 dark:text-[#EFEEF8] dark:placeholder:text-[#8B88A6]',
          )}
        />
        <button
          type="submit"
          disabled={busy || !code.trim()}
          className="inline-flex h-11 shrink-0 items-center justify-center rounded-xl bg-[#5B2EE5] px-5 text-[14px] font-semibold text-white transition-colors hover:bg-[#4D22D2] disabled:opacity-60"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Join'}
        </button>
      </div>
      {schoolCodeError && (
        <p role="alert" className="text-sm font-medium text-[#A32B16] dark:text-[#FF9C87]">
          {schoolCodeError}
        </p>
      )}
    </form>
  );
}
